// ENSAIO DE LAYOUT — a pergunta "isso ia funcionar?" respondida antes de valer.
//
// Duas perguntas moram aqui, e são simétricas:
//
//  1. ABA "ARQUIVO" (entrada): o ente mandou um arquivo — ele entra? Qual
//     layout o reconhece, o que a tradução faria com ele, o que se perderia.
//  2. ABA "MOVIMENTO" (saída): se eu trocar o layout desta unidade, como fica o
//     arquivo que ela recebe? É a pergunta que antecede toda atribuição, e
//     antes dela só havia uma resposta possível: atribuir e ver no mês seguinte.
//
// Nenhuma das duas muda nada. O ensaio não abre folha, não escreve na troca de
// arquivos, não guarda blob e não toca `movimentoEmitido` — o arquivo de saída
// sai marcado `-ENSAIO` no nome justamente para não ser confundido com o
// entregue, porque um movimento de ensaio importado por engano no ERP seria um
// desconto que ninguém instruiu.
//
// O cartão "Ensaios recentes" fecha o ciclo: é o registro de quem tentou o quê,
// e ele não carrega conteúdo nenhum do arquivo por construção (FR-029).

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  EstadoErro,
  EstadoVazio,
  Pill,
  Skeleton,
  Tabs,
  type Column,
  type PillVariant,
} from "@atlas/ui/web";
import type { AtlasClient, LayoutEnsaio } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { EnsaioPainel, baixarBlob } from "../../components/EnsaioPainel";
import { LayoutChip } from "../../components/LayoutChip";
import { RelatorioGeracaoPainel } from "../prefeitura/troca-arquivos-relatorio";

type HubResposta = Awaited<ReturnType<AtlasClient["admin"]["layouts"]["hub"]>>;

// ============================================================ peças

const rotulo: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: "var(--text-muted)" };
const campo: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 13,
};

const VEREDITO_PILL: Record<LayoutEnsaio["veredito"], { rotulo: string; variant: PillVariant }> = {
  reconhecido: { rotulo: "reconhecido", variant: "aceita" },
  ambiguo: { rotulo: "ambíguo", variant: "pendente" },
  recusado: { rotulo: "recusado", variant: "rejeitada" },
  gerado: { rotulo: "gerado", variant: "averbado" },
};

const SENTIDO_ROTULO: Record<string, string> = { entrada: "retorno", saida: "movimento" };

const fmtDataHora = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

/**
 * Base64 → bytes.
 *
 * O ensaio de saída vem em `formato=json` — o arquivo e o relatório na mesma
 * resposta. Pedir o binário e depois o relatório seriam DOIS ensaios, dois
 * registros e duas chances de o que se lê não ser o que se salvou.
 */
function bytesDeBase64(b64: string): ArrayBuffer {
  const bruto = atob(b64);
  const buffer = new ArrayBuffer(bruto.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < bruto.length; i++) out[i] = bruto.charCodeAt(i);
  return buffer;
}

// ============================================================ a tela

export function AdminLayoutsEnsaio() {
  const [aba, setAba] = useState<"arquivo" | "movimento">("arquivo");

  // A MESMA CONSULTA DO HUB, e a mesma chave: os seletores desta tela querem
  // exatamente o que o hub já carregou (unidades + catálogo), e repetir a
  // chamada só para preencher dois `<select>` gastaria uma ida à toa.
  const hub = useQuery({
    queryKey: ["admin", "layouts", "hub"],
    queryFn: () => atlas.admin.layouts.hub(),
  });

  const ensaios = useQuery({
    queryKey: ["admin", "layouts", "ensaios"],
    queryFn: () => atlas.admin.layouts.ensaios({ limite: 20 }),
  });

  const unidades = useMemo(
    () => (hub.data?.unidades ?? []).map((u) => ({ id: u.id, nome: u.nome })),
    [hub.data],
  );
  const nomePorId = useMemo(() => new Map(unidades.map((u) => [u.id, u.nome])), [unidades]);
  const catalogo = hub.data?.catalogo ?? [];
  const deEntrada = useMemo(
    // So' arquivo da FOLHA: o mensal da consignataria (#165) nao e' pacote de
    // retorno, e a API recusa ensaia-lo aqui.
    () =>
      catalogo
        .filter((c) => c.sentido === "entrada" && c.finalidade !== "entrada_consignataria")
        .map((c) => ({ codigo: c.codigo, nome: c.nome })),
    [catalogo],
  );
  // O retorno à consignatária é saída, mas não é movimento: não se ensaia aqui.
  const deSaida = useMemo(
    () => catalogo.filter((c) => c.sentido === "saida" && c.finalidade !== "retorno_consignataria"),
    [catalogo],
  );

  const colunasEnsaios: Column<LayoutEnsaio>[] = [
    { key: "quando", header: "Quando", width: 140, render: (e) => fmtDataHora(e.iniciadoEm) },
    { key: "quem", header: "Quem", wrap: true, render: (e) => e.atorNome },
    {
      key: "unidade",
      header: "Unidade",
      wrap: true,
      render: (e) =>
        e.unidadeId === null ? (
          <span style={{ color: "var(--text-dim)" }}>sem unidade</span>
        ) : (
          <>{nomePorId.get(e.unidadeId) ?? `#${e.unidadeId}`}</>
        ),
    },
    { key: "sentido", header: "Sentido", width: 110, render: (e) => SENTIDO_ROTULO[e.sentido] ?? e.sentido },
    {
      key: "layout",
      header: "Layout",
      wrap: true,
      render: (e) => {
        const codigo = e.layoutReconhecido ?? e.layoutPedido;
        const def = catalogo.find((c) => c.codigo === codigo);
        if (!def) return <code style={{ fontFamily: "var(--font-mono)" }}>{codigo}</code>;
        return (
          <LayoutChip
            tamanho="sm"
            nome={def.nome}
            codigo={def.codigo}
            versao={def.versao}
            situacao={def.situacao}
            padrao={def.padrao}
          />
        );
      },
    },
    {
      key: "veredito",
      header: "Veredito",
      width: 130,
      render: (e) => (
        <Pill variant={VEREDITO_PILL[e.veredito].variant}>{VEREDITO_PILL[e.veredito].rotulo}</Pill>
      ),
    },
    {
      key: "tamanho",
      header: "Bytes",
      align: "right",
      render: (e) => e.tamanhoBytes.toLocaleString("pt-BR"),
    },
  ];

  return (
    // `minmax(0, 1fr)` (T081): a tabela de ensaios recentes tem sete colunas e,
    // sem o mínimo em zero, ela esticaria a coluna do grid e faria a PÁGINA
    // rolar de lado a 1024 px. Com ele, quem rola é o quadro (FR-038).
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      {/* ---------------------------------------------------------- cabeçalho */}
      <div>
        <Link to="/averbadora/layouts" style={{ fontSize: 13 }}>
          ← Hub
        </Link>
        <h1 style={{ margin: "6px 0 0", fontSize: "1.6rem" }}>Ensaio de layout</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-muted)", maxWidth: 760 }}>
          Reconhece, traduz ou gera um arquivo contra um layout sem folha aberta e sem gravar nada. Serve para
          responder "isso ia funcionar?" antes de atribuir, e para conferir o arquivo de um órgão que ainda nem
          é cliente.
        </p>
      </div>

      <Tabs
        tabs={[
          { key: "arquivo", label: "Arquivo" },
          { key: "movimento", label: "Movimento" },
        ]}
        activeKey={aba}
        onChange={(k) => setAba(k as "arquivo" | "movimento")}
      />

      {aba === "arquivo" ? (
        <EnsaioPainel
          modo="averbadora"
          layouts={deEntrada}
          unidades={unidades}
          competenciaPadrao={hub.data?.competenciaReferencia ?? ""}
        />
      ) : (
        <EnsaioDeMovimento
          unidades={unidades}
          layouts={deSaida}
          competenciaPadrao={hub.data?.competenciaReferencia ?? ""}
        />
      )}

      {/* ---------------------------------------------------------- ensaios recentes */}
      <section
        aria-labelledby="ensaios-recentes"
        style={{ display: "grid", gap: 10, gridTemplateColumns: "minmax(0, 1fr)" }}
      >
        <h2 id="ensaios-recentes" style={{ margin: 0, fontSize: 16 }}>
          Ensaios recentes
        </h2>
        <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)" }}>
          O registro de quem ensaiou o quê. Não guarda uma linha sequer do arquivo — só quem, quando, layout,
          tamanho, hash e veredito.
        </p>
        {ensaios.isError ? (
          <EstadoErro
            mensagem="Não foi possível carregar os ensaios."
            onTentarDeNovo={() => ensaios.refetch()}
          />
        ) : (
          <DataTable
            caption="Ensaios de layout registrados, com quem ensaiou, a unidade, o layout e o veredito"
            loading={!ensaios.data}
            columns={colunasEnsaios}
            rows={ensaios.data?.ensaios ?? []}
            rowKey={(e) => e.id}
            emptyState={
              <EstadoVazio
                titulo="Nenhum ensaio ainda"
                orientacao="Envie um arquivo na aba Arquivo, ou gere um movimento na aba Movimento: o resultado aparece aqui."
              />
            }
          />
        )}
      </section>
    </div>
  );
}

// ============================================================ aba Movimento

/**
 * "COMO FICARIA O MOVIMENTO DESTA UNIDADE NO LAYOUT X?"
 *
 * O arquivo NÃO baixa sozinho ao gerar, e é decisão: metade das vezes quem
 * gera quer o relatório (quantas dívidas foram somadas numa linha só? a ADF
 * coube no arquivo?), não o arquivo. Baixar a cada clique encheria a pasta de
 * Downloads de quem só estava conferindo.
 */
function EnsaioDeMovimento({
  unidades,
  layouts,
  competenciaPadrao,
}: {
  unidades: { id: number; nome: string }[];
  layouts: HubResposta["catalogo"];
  competenciaPadrao: string;
}) {
  const [unidade, setUnidade] = useState("");
  const [competencia, setCompetencia] = useState(competenciaPadrao);
  const [layout, setLayout] = useState("");

  // Mesma razão do painel de entrada: a competência de referência vem com o
  // hub, e a aba pode ser aberta antes de ela chegar.
  useEffect(() => {
    if (competenciaPadrao) setCompetencia((atual) => atual || competenciaPadrao);
  }, [competenciaPadrao]);

  const gerar = useMutation({
    mutationFn: () =>
      atlas.admin.layouts.ensaioMovimento({
        unidadeId: Number(unidade),
        ...(competencia.trim() ? { competencia: competencia.trim() } : {}),
        ...(layout ? { layout } : {}),
      }),
  });

  const r = gerar.data;
  const def = r ? layouts.find((c) => c.codigo === r.layout.codigo) : undefined;

  return (
    <div style={{ display: "grid", gap: 14, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <Card>
        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))",
          }}
        >
          <div style={{ display: "grid", gap: 4 }}>
            <label htmlFor="ensaio-mov-unidade" style={rotulo}>
              Unidade
            </label>
            <select
              id="ensaio-mov-unidade"
              style={campo}
              value={unidade}
              onChange={(e) => setUnidade(e.target.value)}
            >
              <option value="">escolha a unidade</option>
              {unidades.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.nome}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            <label htmlFor="ensaio-mov-competencia" style={rotulo}>
              Competência
            </label>
            <input
              id="ensaio-mov-competencia"
              style={campo}
              value={competencia}
              placeholder="AAAAMM"
              inputMode="numeric"
              onChange={(e) => setCompetencia(e.target.value)}
            />
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            <label htmlFor="ensaio-mov-layout" style={rotulo}>
              Layout de movimento
            </label>
            <select
              id="ensaio-mov-layout"
              style={campo}
              value={layout}
              onChange={(e) => setLayout(e.target.value)}
            >
              <option value="">o que está em vigor nesta unidade</option>
              {layouts.map((c) => (
                <option key={c.codigo} value={c.codigo}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
          O arquivo sai marcado <code style={{ fontFamily: "var(--font-mono)" }}>-ENSAIO</code> no nome. Nada
          é emitido, nada entra no histórico de trocas e a folha não é marcada como enviada.
        </p>
        <div style={{ marginTop: 12 }}>
          <Button onClick={() => gerar.mutate()} disabled={!unidade || gerar.isPending}>
            {gerar.isPending ? "Gerando…" : "Gerar ensaio"}
          </Button>
        </div>
      </Card>

      {gerar.isError ? (
        <EstadoErro
          mensagem={`Não foi possível gerar: ${(gerar.error as Error).message}`}
          onTentarDeNovo={() => gerar.mutate()}
        />
      ) : null}

      {gerar.isPending ? <Skeleton linhas={4} altura={18} /> : null}

      {r ? (
        <>
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <LayoutChip
                tamanho="sm"
                nome={def?.nome ?? r.layout.codigo}
                codigo={r.layout.codigo}
                versao={r.layout.versao}
                {...(def ? { situacao: def.situacao, padrao: def.padrao } : {})}
              />
              <code style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{r.nomeArquivo}</code>
              <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
                {r.registro.tamanhoBytes.toLocaleString("pt-BR")} bytes
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => baixarBlob(new Blob([bytesDeBase64(r.arquivoBase64)]), r.nomeArquivo)}
              >
                Baixar arquivo
              </Button>
            </div>
          </Card>
          <RelatorioGeracaoPainel
            relatorio={r.relatorio}
            {...(def ? { identidade: { nome: def.nome, situacao: def.situacao, padrao: def.padrao } } : {})}
          />
        </>
      ) : null}
    </div>
  );
}
